import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import Textarea from "../../components/ui/Textarea";
import ChipMultiSelect from "../../components/ui/ChipMultiSelect";
import ValidatedUploadField from "../../components/ui/ValidatedUploadField";
import { ONBOARDING_ACCEPTED_FILE_TYPES } from "../onboarding/constants";
import type { UploadedFileMeta } from "../onboarding/types";
import {
  extractPrivateFundingFromUrl,
  readPrivateFundingList,
  setActivePrivateFunding,
  upsertPrivateFundingOpportunity,
} from "./service";
import type { PrivateFundingMode, PrivateFundingOpportunity } from "./types";

type EditorState = {
  id?: string;
  mode: PrivateFundingMode;
  funderName: string;
  url: string;
  deadline: string;
  amount: string;
  tags: string[];
  summary: string;
  whatTheyAreLookingFor: string;
  notes: string;
  fileUploads: UploadedFileMeta[];
  isPrivate: boolean;
  createdAt?: string;
};

const emptyEditor = (mode: PrivateFundingMode = "url"): EditorState => ({
  mode,
  funderName: "",
  url: "",
  deadline: "",
  amount: "",
  tags: [],
  summary: "",
  whatTheyAreLookingFor: "",
  notes: "",
  fileUploads: [],
  isPrivate: true,
});

export default function PrivateFundingWorkspace({
  userKey,
  title = "Private funding opportunities",
  subtitle = "Track private or manual opportunities outside the public discovery feed.",
  onUseInGrantWriter,
}: {
  userKey: string;
  title?: string;
  subtitle?: string;
  onUseInGrantWriter?: (item: PrivateFundingOpportunity) => void;
}) {
  const [items, setItems] = useState<PrivateFundingOpportunity[]>([]);
  const [open, setOpen] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editor, setEditor] = useState<EditorState>(emptyEditor());

  useEffect(() => {
    setItems(readPrivateFundingList(userKey));
  }, [userKey]);

  const sortedItems = useMemo(
    () =>
      [...items].sort(
        (left, right) =>
          new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime()
      ),
    [items]
  );

  const openCreate = (mode: PrivateFundingMode) => {
    setEditor(emptyEditor(mode));
    setOpen(true);
  };

  const openEdit = (item: PrivateFundingOpportunity) => {
    setEditor({
      id: item.id,
      mode: item.mode,
      funderName: item.funderName,
      url: item.url ?? "",
      deadline: item.deadline ?? "",
      amount: item.amount ?? "",
      tags: item.tags,
      summary: item.summary,
      whatTheyAreLookingFor: item.whatTheyAreLookingFor,
      notes: item.notes,
      fileUploads: item.fileUploads,
      isPrivate: item.isPrivate,
      createdAt: item.createdAt,
    });
    setOpen(true);
  };

  const saveEditor = () => {
    if (editor.mode === "url" && !editor.url.trim()) {
      toast.error("Add a URL before saving.");
      return;
    }

    if (!editor.funderName.trim() && editor.mode === "url") {
      toast.error("Extract or add a funder name before saving.");
      return;
    }

    if (editor.mode === "manual" && !editor.whatTheyAreLookingFor.trim()) {
      toast.error("Describe what they are looking for.");
      return;
    }

    setSaving(true);
    const saved = upsertPrivateFundingOpportunity(userKey, {
      id: editor.id,
      createdAt: editor.createdAt,
      mode: editor.mode,
      funderName: editor.funderName || "Private funding opportunity",
      url: editor.url || undefined,
      deadline: editor.deadline || undefined,
      amount: editor.amount || undefined,
      tags: editor.tags,
      summary: editor.summary,
      whatTheyAreLookingFor: editor.whatTheyAreLookingFor,
      notes: editor.notes,
      fileUploads: editor.fileUploads,
      isPrivate: editor.isPrivate,
    });

    const nextItems = readPrivateFundingList(userKey);
    setItems(nextItems);
    setSaving(false);
    setOpen(false);
    toast.success("Private funding opportunity saved.");

    if (onUseInGrantWriter) {
      setActivePrivateFunding(userKey, saved.id);
    }
  };

  const handleExtract = async () => {
    if (!editor.url.trim()) {
      toast.error("Paste a URL to extract from.");
      return;
    }

    try {
      setExtracting(true);
      const extracted = await extractPrivateFundingFromUrl(editor.url.trim());
      setEditor((current) => ({
        ...current,
        ...extracted,
      }));
      toast.success("URL details extracted. You can edit everything before saving.");
    } catch {
      toast.error("That URL could not be parsed.");
    } finally {
      setExtracting(false);
    }
  };

  return (
    <div className="rounded-[24px] border border-[#E5E7EB] bg-white p-4 shadow-sm sm:p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="text-xl font-semibold text-[#111827]">{title}</div>
          <div className="mt-1 text-sm text-[#6B7280]">{subtitle}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => openCreate("url")}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-2xl bg-[#0F172A] px-4 py-2 text-sm font-medium text-white"
          >
            <span className="material-icons text-[18px]">add</span>
            <span>Add from URL</span>
          </button>
          <button
            type="button"
            onClick={() => openCreate("manual")}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-2xl border border-[#D6DADF] bg-white px-4 py-2 text-sm font-medium text-[#111827]"
          >
            <span className="material-icons text-[18px]">add</span>
            <span>Manual add</span>
          </button>
        </div>
      </div>

      {sortedItems.length === 0 ? (
        <div className="mt-4 rounded-3xl border border-dashed border-[#CBD5E1] bg-[#F8FAFC] px-4 py-8 text-center text-sm text-[#64748B]">
          No private opportunities yet. Add one from a URL or describe it manually.
        </div>
      ) : (
        <div className="mt-4 grid gap-3">
          {sortedItems.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => openEdit(item)}
              className="rounded-3xl border border-[#E5E7EB] bg-[#F8FAFC] p-4 text-left transition hover:border-[#C3D8C4] hover:bg-white"
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <div className="text-base font-semibold text-[#111827]">
                    {item.funderName || "Private funding opportunity"}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-2 text-xs text-[#475569]">
                    {item.amount ? <span>Amount: {item.amount}</span> : null}
                    {item.deadline ? <span>Deadline: {item.deadline}</span> : null}
                    {item.isPrivate ? (
                      <span className="rounded-full bg-[#E0F2FE] px-2 py-1 text-[#0369A1]">
                        Private
                      </span>
                    ) : null}
                  </div>
                </div>
                {onUseInGrantWriter ? (
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      setActivePrivateFunding(userKey, item.id);
                      onUseInGrantWriter(item);
                    }}
                    className="inline-flex min-h-[40px] items-center rounded-full bg-[#166534] px-4 py-2 text-sm font-medium text-white"
                  >
                    Use in Grant Writer
                  </button>
                ) : null}
              </div>
            </button>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40">
          <div className="absolute inset-x-0 bottom-0 max-h-[92vh] overflow-y-auto rounded-t-[32px] bg-white p-5 shadow-2xl sm:inset-y-0 sm:right-0 sm:left-auto sm:w-[680px] sm:rounded-none sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xl font-semibold text-[#111827]">
                  {editor.id ? "Edit private opportunity" : "Add private opportunity"}
                </div>
                <div className="mt-1 text-sm text-[#6B7280]">
                  Save manual or private opportunities to your personal workspace list.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-[#E5E7EB]"
              >
                <span className="material-icons text-[20px]">close</span>
              </button>
            </div>

            <div className="mt-5 flex gap-2">
              {(["url", "manual"] as PrivateFundingMode[]).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setEditor((current) => ({ ...current, mode }))}
                  className={`min-h-[42px] rounded-full px-4 py-2 text-sm font-medium ${
                    editor.mode === mode
                      ? "bg-[#0F172A] text-white"
                      : "border border-[#E5E7EB] bg-white text-[#111827]"
                  }`}
                >
                  {mode === "url" ? "Add from URL" : "Manual describe"}
                </button>
              ))}
            </div>

            {editor.mode === "url" ? (
              <div className="mt-5 space-y-4">
                <label className="block">
                  <div className="mb-2 text-sm font-semibold text-[#111827]">URL</div>
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <Input
                      value={editor.url}
                      onChange={(event) =>
                        setEditor((current) => ({ ...current, url: event.target.value }))
                      }
                      placeholder="https://example.org/funding-opportunity"
                      className="min-h-[44px] text-base"
                    />
                    <Button
                      className="min-h-[44px] rounded-2xl"
                      onClick={handleExtract}
                      disabled={extracting}
                    >
                      {extracting ? "Extracting..." : "Extract"}
                    </Button>
                  </div>
                </label>

                <label className="block">
                  <div className="mb-2 text-sm font-semibold text-[#111827]">Funder name</div>
                  <Input
                    value={editor.funderName}
                    onChange={(event) =>
                      setEditor((current) => ({ ...current, funderName: event.target.value }))
                    }
                    placeholder="Funder name"
                    className="min-h-[44px] text-base"
                  />
                </label>

                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="block">
                    <div className="mb-2 text-sm font-semibold text-[#111827]">Deadline</div>
                    <Input
                      type="date"
                      value={editor.deadline}
                      onChange={(event) =>
                        setEditor((current) => ({ ...current, deadline: event.target.value }))
                      }
                      className="min-h-[44px] text-base"
                    />
                  </label>

                  <label className="block">
                    <div className="mb-2 text-sm font-semibold text-[#111827]">Amount</div>
                    <Input
                      value={editor.amount}
                      onChange={(event) =>
                        setEditor((current) => ({ ...current, amount: event.target.value }))
                      }
                      placeholder="e.g. £25k–£100k"
                      className="min-h-[44px] text-base"
                    />
                  </label>
                </div>

                <label className="block">
                  <div className="mb-2 text-sm font-semibold text-[#111827]">Tags</div>
                  <ChipMultiSelect
                    options={editor.tags}
                    value={editor.tags}
                    onChange={(value) => setEditor((current) => ({ ...current, tags: value }))}
                    allowCustom
                    customLabel="Add tag"
                    placeholder="Add tags"
                  />
                </label>

                <label className="block">
                  <div className="mb-2 text-sm font-semibold text-[#111827]">Summary</div>
                  <Textarea
                    value={editor.summary}
                    onChange={(event) =>
                      setEditor((current) => ({ ...current, summary: event.target.value }))
                    }
                    placeholder="Summarise the opportunity, fit, and any key details."
                    className="min-h-[120px] text-base"
                  />
                </label>
              </div>
            ) : (
              <div className="mt-5 space-y-4">
                <label className="block">
                  <div className="mb-2 text-sm font-semibold text-[#111827]">
                    What they are looking for *
                  </div>
                  <Textarea
                    value={editor.whatTheyAreLookingFor}
                    onChange={(event) =>
                      setEditor((current) => ({
                        ...current,
                        funderName: current.funderName || "Manual private opportunity",
                        whatTheyAreLookingFor: event.target.value,
                        summary: current.summary || event.target.value,
                      }))
                    }
                    placeholder="Describe the brief, eligibility, theme, or request."
                    className="min-h-[130px] text-base"
                  />
                </label>

                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="block">
                    <div className="mb-2 text-sm font-semibold text-[#111827]">Deadline</div>
                    <Input
                      type="date"
                      value={editor.deadline}
                      onChange={(event) =>
                        setEditor((current) => ({ ...current, deadline: event.target.value }))
                      }
                      className="min-h-[44px] text-base"
                    />
                  </label>

                  <label className="block">
                    <div className="mb-2 text-sm font-semibold text-[#111827]">
                      Amount / amount band
                    </div>
                    <Input
                      value={editor.amount}
                      onChange={(event) =>
                        setEditor((current) => ({ ...current, amount: event.target.value }))
                      }
                      placeholder="e.g. £50k or Under £25k"
                      className="min-h-[44px] text-base"
                    />
                  </label>
                </div>

                <label className="block">
                  <div className="mb-2 text-sm font-semibold text-[#111827]">Notes</div>
                  <Textarea
                    value={editor.notes}
                    onChange={(event) =>
                      setEditor((current) => ({ ...current, notes: event.target.value }))
                    }
                    placeholder="Optional notes, contacts, or reminders."
                    className="min-h-[120px] text-base"
                  />
                </label>

                <label className="block">
                  <div className="mb-2 text-sm font-semibold text-[#111827]">File upload</div>
                  <ValidatedUploadField
                    value={editor.fileUploads}
                    onChange={(value) =>
                      setEditor((current) => ({ ...current, fileUploads: value }))
                    }
                    accept={ONBOARDING_ACCEPTED_FILE_TYPES}
                    multiple={false}
                    helperText="Attach one optional PDF, DOCX, or PPTX file."
                  />
                </label>
              </div>
            )}

            <label className="mt-5 flex min-h-[44px] items-center gap-3 rounded-2xl border border-[#E5E7EB] px-4 py-3">
              <input
                type="checkbox"
                checked={editor.isPrivate}
                onChange={(event) =>
                  setEditor((current) => ({ ...current, isPrivate: event.target.checked }))
                }
                className="h-5 w-5 rounded"
              />
              <div>
                <div className="text-sm font-semibold text-[#111827]">Private opportunity</div>
                <div className="text-xs text-[#6B7280]">
                  Keep this item in your personal or workspace-only list.
                </div>
              </div>
            </label>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
              <Button
                variant="outline"
                className="min-h-[48px] rounded-2xl"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button
                className="min-h-[48px] rounded-2xl"
                onClick={saveEditor}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save opportunity"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
